/**
 * BulkRateAssignmentModal - Modal for bulk-assigning rates to multiple entities
 * Supports Units, Groups, and Companies
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { DatePicker } from '@/components/ui/date-picker';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, AlertCircle, Calendar, Users, Building2, Zap, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EnergyRate, BulkRateAssignmentRequest, BulkRateAssignmentResponse } from '@/types/rates';
import { format } from 'date-fns';

interface EntityInfo {
  id: string;
  name: string;
  currentRate?: string;
}

interface BulkRateAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: BulkRateAssignmentRequest) => Promise<BulkRateAssignmentResponse | null>;
  entityType: 'company' | 'group' | 'unit';
  selectedEntities: EntityInfo[];
  rates: EnergyRate[];
  isSubmitting?: boolean;
}

const entityTypeConfig = {
  company: {
    label: 'Companies',
    singularLabel: 'Company',
    icon: Building2,
    description: 'Rate will apply to all units under these companies',
  },
  group: {
    label: 'Groups/Sites',
    singularLabel: 'Group',
    icon: Users,
    description: 'Rate will apply to all units under these groups/sites',
  },
  unit: {
    label: 'Units',
    singularLabel: 'Unit',
    icon: Zap,
    description: 'Rate will be assigned directly to these units',
  },
};

export function BulkRateAssignmentModal({
  open,
  onClose,
  onSubmit,
  entityType,
  selectedEntities,
  rates,
  isSubmitting = false,
}: BulkRateAssignmentModalProps) {
  const config = entityTypeConfig[entityType];
  const EntityIcon = config.icon;

  // Form state
  const [formData, setFormData] = useState<{
    rateId: string;
    effectiveFrom: Date | undefined;
    effectiveTo: Date | undefined;
    hasEndDate: boolean;
  }>({
    rateId: '',
    effectiveFrom: new Date(),
    effectiveTo: undefined,
    hasEndDate: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<BulkRateAssignmentResponse | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        rateId: rates[0]?.id || '',
        effectiveFrom: new Date(),
        effectiveTo: undefined,
        hasEndDate: false,
      });
      setErrors({});
      setResult(null);
    }
  }, [open, rates]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.rateId) {
      newErrors.rateId = 'Please select a rate profile';
    }

    if (!formData.effectiveFrom) {
      newErrors.effectiveFrom = 'Start date is required';
    }

    if (formData.hasEndDate && formData.effectiveTo && formData.effectiveFrom) {
      if (formData.effectiveTo < formData.effectiveFrom) {
        newErrors.effectiveTo = 'End date must be after start date';
      }
    }

    if (selectedEntities.length === 0) {
      newErrors.entities = 'No entities selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData: BulkRateAssignmentRequest = {
      entityType,
      entityIds: selectedEntities.map(e => e.id),
      rateId: formData.rateId,
      effectiveFrom: formData.effectiveFrom
        ? format(formData.effectiveFrom, 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'),
      effectiveTo: formData.hasEndDate && formData.effectiveTo
        ? format(formData.effectiveTo, 'yyyy-MM-dd')
        : undefined,
    };

    const response = await onSubmit(submitData);
    if (response) {
      setResult(response);
    }
  };

  const selectedRate = rates.find(r => r.id === formData.rateId);

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  // Show result screen after successful submission
  if (result) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {result.errors.length === 0 ? (
                <>
                  <Check className="h-5 w-5 text-[#8FB560]" />
                  Bulk Assignment Complete
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Bulk Assignment Completed with Issues
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-lg bg-[#8FB560]/10">
                <div className="text-2xl font-bold text-[#8FB560]">{result.created}</div>
                <div className="text-xs text-muted-foreground">Created</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-amber-500/10">
                <div className="text-2xl font-bold text-amber-500">{result.skipped}</div>
                <div className="text-xs text-muted-foreground">Skipped</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-destructive/10">
                <div className="text-2xl font-bold text-destructive">{result.errors.length}</div>
                <div className="text-xs text-muted-foreground">Errors</div>
              </div>
            </div>

            {/* Errors list */}
            {result.errors.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Errors</Label>
                <ScrollArea className="h-[150px] rounded-md border p-2">
                  {result.errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm py-1">
                      <X className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{err.error}</span>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}

            {result.skipped > 0 && (
              <p className="text-sm text-muted-foreground">
                Skipped assignments had overlapping date ranges with existing assignments.
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={handleClose}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EntityIcon className="h-5 w-5 text-[#8FB560]" />
            Bulk Assign Rate to {config.label}
          </DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Selected Entities Preview */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Selected {config.label} ({selectedEntities.length})
            </Label>
            <ScrollArea className="h-[100px] rounded-md border p-2">
              <div className="flex flex-wrap gap-2">
                {selectedEntities.map(entity => (
                  <Badge key={entity.id} variant="secondary" className="text-xs">
                    {entity.name}
                  </Badge>
                ))}
              </div>
            </ScrollArea>
            {errors.entities && (
              <p className="text-sm text-destructive">{errors.entities}</p>
            )}
          </div>

          {/* Rate Selection */}
          <div className="space-y-2">
            <Label htmlFor="rateId">Rate Profile *</Label>
            <Select
              value={formData.rateId}
              onValueChange={value => {
                setFormData(prev => ({ ...prev, rateId: value }));
                if (errors.rateId) {
                  setErrors(prev => {
                    const { rateId: _, ...rest } = prev;
                    return rest;
                  });
                }
              }}
            >
              <SelectTrigger className={cn(errors.rateId && 'border-destructive')}>
                <SelectValue placeholder="Select a rate profile" />
              </SelectTrigger>
              <SelectContent>
                {rates.map(rate => (
                  <SelectItem key={rate.id} value={rate.id}>
                    <div className="flex flex-col">
                      <span>{rate.name}</span>
                      <span className="text-xs text-muted-foreground">
                        Import: ${rate.gridImportRate}/kWh | Sell: ${rate.gridSellRate}/kWh
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.rateId && <p className="text-sm text-destructive">{errors.rateId}</p>}
          </div>

          {/* Selected Rate Preview */}
          {selectedRate && (
            <div className="border border-border rounded-lg p-3 bg-muted/30">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-[#8FB560]" />
                <span className="font-semibold text-sm">{selectedRate.name}</span>
                {selectedRate.utilityProvider && (
                  <Badge variant="outline" className="text-[10px]">
                    {selectedRate.utilityProvider}
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Import:</span>
                  <span className="ml-1 font-mono">${selectedRate.gridImportRate}/kWh</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Sell:</span>
                  <span className="ml-1 font-mono">${selectedRate.gridSellRate}/kWh</span>
                </div>
              </div>
            </div>
          )}

          {/* Effective Period */}
          <div className="border border-border rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold text-sm">Effective Period</h3>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <DatePicker
                date={formData.effectiveFrom}
                onDateChange={date => {
                  setFormData(prev => ({ ...prev, effectiveFrom: date }));
                  if (errors.effectiveFrom) {
                    setErrors(prev => {
                      const { effectiveFrom: _, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                placeholder="Select start date"
                className={cn(errors.effectiveFrom && 'border-destructive')}
              />
              {errors.effectiveFrom && (
                <p className="text-sm text-destructive">{errors.effectiveFrom}</p>
              )}
            </div>

            {/* Has End Date Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="hasEndDate" className="text-sm font-medium">
                  Set End Date
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Leave unchecked for ongoing assignment
                </p>
              </div>
              <Switch
                id="hasEndDate"
                checked={formData.hasEndDate}
                onCheckedChange={checked => {
                  setFormData(prev => ({
                    ...prev,
                    hasEndDate: checked,
                    effectiveTo: checked ? prev.effectiveTo : undefined,
                  }));
                }}
              />
            </div>

            {/* End Date (conditional) */}
            {formData.hasEndDate && (
              <div className="space-y-2">
                <Label>End Date</Label>
                <DatePicker
                  date={formData.effectiveTo}
                  onDateChange={date => {
                    setFormData(prev => ({ ...prev, effectiveTo: date }));
                    if (errors.effectiveTo) {
                      setErrors(prev => {
                        const { effectiveTo: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="Select end date"
                  className={cn(errors.effectiveTo && 'border-destructive')}
                />
                {errors.effectiveTo && (
                  <p className="text-sm text-destructive">{errors.effectiveTo}</p>
                )}
              </div>
            )}
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-500">
              Assignments with overlapping date ranges will be skipped. Make sure to check
              existing assignments before bulk assigning.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#8FB560] hover:bg-[#7a9d4e]"
              disabled={isSubmitting || selectedEntities.length === 0}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                `Assign to ${selectedEntities.length} ${selectedEntities.length === 1 ? config.singularLabel : config.label}`
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
